
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SchoolUser } from "@/types/users";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";

export const useUsers = () => {
  const [users, setUsers] = useState<SchoolUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [userToDelete, setUserToDelete] = useState<SchoolUser | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const pageSize = 10;
  const { toast } = useToast();
  
  const debouncedSearch = useDebounce(searchQuery, 500);

  const fetchUsers = async (search: string = "", currentPage: number = 1, status: string | null = null) => {
    try {
      setLoading(true);
      let query = supabase
        .from('school_users')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      // Add search conditions if search string exists
      if (search) {
        query = query
          .or(`email.ilike.%${search}%,mobile_number.ilike.%${search}%,school_id.ilike.%${search}%`)
          .or(`role.eq.admin,role.eq.staff,role.eq.teacher`);
      }

      // Add status filter if specified
      if (status === 'approved') {
        query = query.eq('approved', true);
      } else if (status === 'unapproved') {
        query = query.eq('approved', false);
      }

      // Add pagination
      query = query.range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      setUsers(data || []);
      setTotalCount(count || 0);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchUsers(searchQuery, newPage, statusFilter);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1); // Reset to first page when search changes
  };

  const handleStatusFilterChange = (status: string | null) => {
    setStatusFilter(status);
    setPage(1); // Reset to first page when filter changes
    fetchUsers(searchQuery, 1, status);
  };

  const handleDelete = async (user: SchoolUser) => {
    try {
      const { error } = await supabase
        .from("school_users")
        .delete()
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "User deleted successfully",
      });

      fetchUsers(searchQuery, page, statusFilter);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete user",
        variant: "destructive",
      });
    } finally {
      setUserToDelete(null);
    }
  };

  useEffect(() => {
    fetchUsers(debouncedSearch, page, statusFilter);
  }, [debouncedSearch]);

  return {
    users,
    loading,
    userToDelete,
    setUserToDelete,
    handleDelete,
    fetchUsers,
    searchQuery,
    handleSearchChange,
    page,
    pageSize,
    totalCount,
    handlePageChange,
    statusFilter,
    handleStatusFilterChange,
  };
};
